import {api, RequestParam} from "@/shared/api";

import {RecordingStatusPanel} from "@/shared/recording/recordingStatusPanel";
import {BaseRecording, BaseRecordingConfig} from "./baseRecording";
import {processTaskDetailsUrlParams, TaskInfoDialog} from "@/features/task/taskInfoDialog";
import {ProjectTimeTable} from "@/features/projecttime/projectTimeTable";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {isProjectTimeDateTimeChangeAllowed} from "@/shared/securityHelper";
import {ProjectTimeStatusPanel} from "@/features/projecttime/projectTimeStatusPanel";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {ProjectTimeStatistics} from "@/features/projecttime/projectTimeStatistics";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {RecordDetailsDialog} from "@/shared/recording/recordDetailsDialog";
import {ProjectTimeDetailsDialog} from "@/features/projecttime/projectTimeDetailsDialog";
import * as RecordingValidationUtil from "@/shared/validations/recordingValidationUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {ProjectTime, ProjectTimeStatus, ReportProjectTime, Task, TaskDetailsResponse} from "@/shared/timr";
import {RecordedEntryStatus} from "@/shared/timrTypes";
import {mapProjectTimeToReportProjectTime} from "@/shared/utils/recordMapper";

export interface ProjectTimeRecordingConfig extends BaseRecordingConfig<ProjectTimeStatus> {
	allowedToChangeBillable: boolean;
	projectTimeAttachmentsEnabled?: boolean;
}

export class ProjectTimeRecordingPage extends BaseRecording<ReportProjectTime, ProjectTimeStatus, ProjectTime> {
	private static PROJECT_TIME_TABLE_PAGE_LENGTH = BaseRecording.RECORDING_TABLE_PAGE_LENGTH;

	declare private projectTimeStatistics: ProjectTimeStatistics;
	declare private taskInfoDialog: TaskInfoDialog;

	constructor(public config: ProjectTimeRecordingConfig) {
		super(config);
	}

	protected init() {
		super.init();
		this.recordingDetailsDialog = this.createRecordingDetailsDialog();

		if (globals.validateProjectTimeOverlaps || globals.validateProjectTimeExistsWorkingTimes) {
			this.initValidationsListeners();
		}

		$(".addButton").toggleClass("disabled", !this.isDateTimeChangeAllowed()).click(() => {
			this.addRecording(this.statusPanel.getLastRecording());
		});

		this.projectTimeStatistics = new ProjectTimeStatistics($("#projectTimeStatistics"));

		$(this.statusPanel).on(RecordingStatusPanel.EVENT_BUTTON_UPDATED, (e, runningRecording: boolean, projectTimeStatus: ProjectTimeStatus) => {
			this.taskInfoDialog?.updateRecordingStatus(projectTimeStatus);
		});

		$(this.recordingTable).on(ProjectTimeTable.EVENT_SHOW_TASK_DETAILS, (e, recording: ReportProjectTime) => {
			this.showInfoDialog(recording.taskId, recording.userId);
		});

		// process task details url params to show task info dialog
		processTaskDetailsUrlParams((taskId: number) => this.showInfoDialog(Number(taskId), this.config.defaultUserId));
	}

	protected showRecording(recording: ReportProjectTime) {
		this.recordingDetailsDialog.show(recording);
	}

	protected getRecordById(recordId: number, link: JQuery) {
		return this.getProjectTimeById(recordId, link);
	}

	protected getProjectTimeById(projectTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		api("reports/getProjectTime.json")
			.get({projectTimeId: projectTimeRecordId})
			.done((data) => {
				if (this.recordingTable.recordingChangeable(data)) {
					this.editRecording(data);
				} else {
					this.showRecording(data);
				}
				RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link);
			});
	}

	protected createStatusPanel(): ProjectTimeStatusPanel {
		return new ProjectTimeStatusPanel({
			panel: $("#projectTimeStatusPanel"),
			fetchStatusURL: "recording/getProjectTimeStatus.json",
			customFieldDefinitions: this.config.customFieldDefinitions
		});
	}

	protected createRecordingTable(): RecordingTable<ReportProjectTime> {
		return new ProjectTimeTable({
			dataUrl: ProjectTimeTable.RECORDING_PROJECT_TIMES_URL,
			element: $("#projectTimeTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: true,
			allowResume: true,
			allowSelect: false,
			linkToReport: "projectTime",
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: false,
			orderable: false,
			clampNote: true,
			allowExpand: true,
			pageLength: 15,
			paging: false,
			excludeElementsDblclickEvent: [".quickSwitchButtonContainer"],
			projectTimeAttachmentsEnabled: this.config.projectTimeAttachmentsEnabled
		});
	}

	protected createRecordingDialog(): RecordDialog<ReportProjectTime> {
		return new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			users: [],
			userSelectionEnabled: false,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
	}

	protected createRecordingDetailsDialog(): RecordDetailsDialog<ReportProjectTime> {
		return new ProjectTimeDetailsDialog({
			title: messages["projectTime.details.header"],
			dialogId: "#projectTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportProjectTime) => this.editRecording(recording)
		});
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddProjectTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.projecttime.title",
				message: "account.expired.projecttime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected addRecording(lastRecording: ProjectTime | null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Project Time - Add Project Time", "Project Time");
		this.recordingDialog.create(lastRecording ? mapProjectTimeToReportProjectTime(lastRecording) : null);
	}

	protected resumeRecording(recording: ReportProjectTime) {
		if (recording.status === RecordedEntryStatus.Changeable) {
			api("recording/resumeProjectTime.json")
				.post({
					id: recording.id,
					timestamp: Date.now()
				})
				.done(() => {
					this.reloadTable();
				});
		}
	}

	protected updateStatistics(status: ProjectTimeStatus) {
		this.projectTimeStatistics.updateStatistics(status);
	}

	protected buildFilterParams(): RequestParam[] {
		const endDate = Temporal.Now.plainDateISO();
		const startDate = endDate.subtract({years: 1});
		return [
			...super.buildFilterParams(),
			{name: "endDate", value: getISODate(endDate)},
			{name: "startDate", value: getISODate(startDate)},
		];
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isProjectTimeDateTimeChangeAllowed();
	}

	private showInfoDialog(taskId: number, userId: number) {
		if (!this.taskInfoDialog) {
			this.taskInfoDialog = new TaskInfoDialog({
				dialogId: "#taskInfoDialog",
				onQuickSwitch: (task: Task) => this.switchToTask(task),
				recordingStatus: (this.statusPanel as ProjectTimeStatusPanel).projectTimeStatus
			});
		}
		void api("tasks/loadTaskDetails.json")
			.get({id: taskId})
			.done((e: TaskDetailsResponse) => {
				this.taskInfoDialog.show(e, userId);
			});
	}

	private switchToTask(task: Task) {
		(this.statusPanel as ProjectTimeStatusPanel).shouldSwitchOrStartRecording(task);
	}

	protected getStatus(record: ReportProjectTime): RecordedEntryStatus {
		return record.status;
	}
}
